import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { AuthenticationService, IWebAppUser, WebAppUser } from '@services/authentication.service'
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { ActivatedRoute } from '@angular/router';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, AccountRoleLabels, UserPreferences } from '@services/accounts.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * ProfileComponent
 */
@Component({
	selector: 'app-profile',
	templateUrl: './profile.component.html',
	styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy {

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Account Role Options */
	accountRoleOptions = Object.entries(AccountRoleLabels).map(([key, label]) => ({
		value: Number(key),
		label
	}));

	/** Disabled */
	disabled = "disabled";

	/** Preferences Changed */
	public PreferencesChanged: boolean = false;

	/** Profile */
	public authUser: any;

	/** List of My Accounts */
	public myAccounts: Account[];

	/** Authenticated User */
	public currentUser: WebAppUser

	/** User being Edited */
	public editUser: WebAppUser

	/** userGUID */
	public urlUserGUID: string
	/** Show Send Email */
	public showSendEmail: boolean = false
	/** Can User Manage Admin Settings */
	public canEditAdminSettings: boolean = false
	/** User Data Source */
	public userDataSource: WebAppUser[]

	/** User Form Controls */
	public userForm: FormGroup //() { return this.userFormGroup; }
	/** User Form Submitted */
	public submitted: boolean = false
	/** User Form Success */
	public success: any
	/** User Form Error */
	public error: any

	/** Selected User */
	public selectedUser: string

	/** User Preferences */
	public Preferences: UserPreferences

	/** Duration Selection */
	public strDuration: string = "01:00:00";

	/**
	 * Default Constructor
	 * @param auth Auth0 Authentication
	 * @param authenticationService Authentication Service
	 * @param formBuilder Form Builder
	 * @param globalFunctions Global Functions
	 * @param activatedRoute Activated Route
	 * @param siteSubscriptions Site Subscriptions
	 */
	constructor(
		public auth: AuthService,
		public authenticationService: AuthenticationService,
		private formBuilder: FormBuilder,
		private globalFunctions: GlobalFunctions,
		private activatedRoute: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,

	) {
		this.userForm = this.formBuilder.group({
			userID: [0],
			userSub: ['', [Validators.required]],
			userEmail: ['', [Validators.required, Validators.email, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
			userNickName: ['', [Validators.required]],
			userName: ['', [Validators.required]],
			userFirstName: [''],
			userLastName: [''],
			userAdminSite: [false],
			userAdminSystems: [false],
			userAdminAccount: [false],
			userDisabled: [false],
			userDefaultAccount: [''],
			userAccessLevel: [0]
		});
	}


	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
		}
		catch { }
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		combineLatest([
			this.auth.user$.pipe(distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.activatedRoute.paramMap.pipe(distinctUntilChanged()),
			this.siteSubscriptions.myAccounts$.pipe(distinctUntilChanged()),
			this.authenticationService.getAllUsers$({}).pipe(distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([authUser, currentUser, paramMap, myAccounts, users]) => !!authUser && !!currentUser && !!paramMap && !!myAccounts && !!users),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: ([authUser, currentUser, paramMap, myAccounts, users]) => {
				try {

					this.authUser = authUser
					this.currentUser = currentUser

					/** Check if we have a supplied GUID for the user */
					this.urlUserGUID = paramMap.get("userGUID") || this.currentUser.UserGUID;

					this.userDataSource = <IWebAppUser[]>users
					this.editUser = <IWebAppUser>this.userDataSource.find(({ UserGUID }) => UserGUID.toLowerCase() == this.urlUserGUID.toLowerCase())
					this.Preferences = this.authenticationService.getPreferences(this.editUser) || new UserPreferences()
					this.strDuration = this.Preferences.TrendDuration || this.strDuration

					this.canEditAdminSettings = false
					if (this.editUser.Sub != this.currentUser.Sub) {
						this.canEditAdminSettings = true
					}
					// this.canEditAdminSettings = true
					this.resetUser(this.editUser)

					this.myAccounts = myAccounts;
					this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
				}
				catch (error) { console.error(error) }
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.auth.user$", false); return; },
		});
	}

	/**
	 * Get all Users
	 */
	async getAllUsers(): Promise<IWebAppUser[] | void> {

		this.userDataSource = [];

		try {
			const AllUsers = await firstValueFrom(
				this.authenticationService.getAllUsers$({}).pipe(
					take(1),
					filter(Boolean)
				)
			);

			// Avoid double assignment race condition
			if (!this.userDataSource.length) {

				this.userDataSource = AllUsers as IWebAppUser[];

				this.currentUser = this.userDataSource.find(
					({ Sub }) => Sub?.toUpperCase() === this.authUser['sub']?.toUpperCase()
				) as IWebAppUser;

				if (this.currentUser) {
					this.currentUser.Preferences = JSON.stringify(this.Preferences);
				}
			}

			return AllUsers;

		} catch (error) { this.globalFunctions.HandleError(error, "Error: authenticationService.getAllUsers$", false); }
	}
	/**
	 * Reset Form Fields
	 * @param user User
	 */
	resetUser(user: IWebAppUser = this.editUser) {

		this.submitted = false;
		this.error = null;
		this.success = null;

		if (user) {
			this.userForm.get("userID")?.setValue(user.UserID)
			this.userForm.get("userSub")?.setValue(user.Sub)
			this.userForm.get("userEmail")?.setValue(user.Email)
			this.userForm.get("userNickName")?.setValue(user.NickName)
			this.userForm.get("userName")?.setValue(user.Name)
			this.userForm.get("userFirstName")?.setValue(user.FirstName)
			this.userForm.get("userLastName")?.setValue(user.LastName)
			this.userForm.get("userAdminSite")?.setValue(user.AdminSite == true)
			this.userForm.get("userAdminSystems")?.setValue(user.AdminSystems == true)
			this.userForm.get("userDisabled")?.setValue(user.Disabled)
			this.userForm.get("userDefaultAccount")?.setValue(user.DefaultAccount)
			this.userForm.get("userAccessLevel")?.setValue(user.AccessLevel)

			if (!this.canEditAdminSettings || this.currentUser.AdminSite != true) {
				this.userForm.get("userAdminSite")?.enable()
				this.userForm.get("userAdminSystems")?.enable()
				this.userForm.get("userDisabled")?.enable()
			}
		}

		/** Update form to not Dirty */
		this.userForm.markAsPristine();

		this.PreferencesChanged = false;

	}

	/**
	 * Update Users
	 * @returns
	 */
	updateUser() {

		/** stop here if form is invalid */
		if (this.userForm["invalid"]) { return; }

		// console.log("updateUser")
		this.globalFunctions.UserConfirm$(`Update ${this.userForm.get("userName")?.value} User Details?`, this.userForm.get("userName")?.value)
			.subscribe(OKResult => {
				if (OKResult) {
					this.submitted = true;
					/** reset alerts on submit */
					this.error = null;
					this.success = null;

					// Update the Edit Users details from the form
					this.editUser.FirstName = this.userForm.get("userFirstName")?.value
					this.editUser.LastName = this.userForm.get("userLastName")?.value
					this.editUser.NickName = this.userForm.get("userNickName")?.value
					this.editUser.Name = this.userForm.get("userName")?.value
					this.editUser.AdminSite = this.userForm.get("userAdminSite")?.value
					this.editUser.AdminSystems = this.userForm.get("userAdminSystems")?.value
					this.editUser.Disabled = this.userForm.get("userDisabled")?.value
					this.editUser.DefaultAccount = this.userForm.get("userDefaultAccount")?.value
					this.editUser.AccessLevel = this.userForm.get("userAccessLevel")?.value

					this.Preferences = JSON.parse(this.editUser.Preferences)
					this.Preferences.TrendDuration = this.strDuration
					let value = JSON.stringify(this.Preferences)
					this.editUser.Preferences = value

					if (this.currentUser.Sub === this.editUser.Sub) {
						localStorage.setItem("myPrefrences", value)
					}

					this.patchUser(this.editUser);

				}

			});
	}

	/**
	 * Patch User Details
	 * @param editUser 
	 */
	async patchUser(editUser: IWebAppUser): Promise<IWebAppUser> {
		try {
			const user = await firstValueFrom(
				this.authenticationService.patchUser$(editUser.Sub, editUser).pipe(
					filter(Boolean)
				)
			);

			this.success = "User Details Updated";

			this.getAllUsers();

			if (this.currentUser.Sub === user.sub) {
				this.authenticationService.setCurrentUser(user);
			}

			this.siteSubscriptions.RefreshUserList(true);

			this.userForm.markAsPristine();
			this.PreferencesChanged = false;

			return user;

		} catch (error) {
			this.globalFunctions.HandleError(error, "Error: authenticationService.patchUser$", false);
			throw error;
		} finally {
			this.submitted = false;
		}
	}

	/**
	 * selectTime
	 */
	selectTime(sDuration: string) {
		this.strDuration = sDuration
		this.PreferencesChanged = ((this.Preferences.TrendDuration || "") != this.strDuration);
	}

	/**
	 * Update User Preferences
	 */
	updatePreferences() {
		this.globalFunctions.UserConfirm$('Save Preferences?', "Save")
			.subscribe(OKResult => {
				if (OKResult) {
					try {
						this.Preferences.TrendDuration = this.strDuration
						let value = JSON.stringify(this.Preferences)
						this.editUser.Preferences = value

						if (this.currentUser.Sub === this.editUser.Sub) {
							localStorage.setItem("myPrefrences", value)
						}
						var _ = this.patchUser(this.editUser)

					} catch (error) { console.error(error) }
				}
			});
	}

}
